/**
 * eilat logic usage:
 * before every add to cart action, check if the product is allowed to be added in the current mode,
 * if not a prompt out/in eilat mode will be shown,
 * 
 * this logic patches fetch and triggers when ever there is a request for cart/add.js
 * 
 * on the product form[data-type="add-to-cart-form"] add the following data:
 *      data-has-eilat-price: true/false
 * and make sure  when ever adding to Eilat cart - pass the product type in the add request
 * {
 *    id: prodVariantID,
 *    quantity: prodQuant,
 *    product_type: "" // the type of the product
 *    ...
 * }
 * 
 * form example:
*       {%- form 'product',
            product,
            ...
            data-has-eilat-price: has_eilat_price,
        -%}
 */

/**
 * Static helper for Eilat cart mode: cart attribute, checks, and leave-eilat prompt.
 * Use: Eilat_handler.setEilatMode(true), Eilat_handler.isEilatMode(cart), etc.
 */
const Eilat_handler = (function () {
    const CART_ATTRIBUTE_EILAT_KEY = (typeof window !== 'undefined' && window.wd_eilat_cart_attribute_key) || '_is_eilat';
    const EILAT_MODE_VALUE = 'true';
    const CART_MODE_MODAL_ID = 'cart-mode-modal';
    const EILAT_METAFIELD_NAMESPACE = 'custom';
    const EILAT_METAFIELD_KEY = 'eilat_price';
    const STOREFRONT_API_VERSION = '2024-07';
    const DEFAULT_MESSAGES = {
        not_eilat_product: 'מוצר זה לא זמין במצב אילת..',
        leave_eilat: 'שים לב - העגלה נמצאת במצב אילת, בפעולה זו תעבור למצב רגיל, כל המוצרים יוסרו מהסל.',
        join_eilat: 'שים לב את/ה עומד לעבור למצב אילת, בפעולה זו כל המוצרים יוסרו מהסל.',
        one_per_type: 'לא ניתן לרכוש באילת יותר ממוצר אחד מהסוג הזה!',
    };
    const MESSAGES = { ...DEFAULT_MESSAGES, ...(typeof window !== 'undefined' && window.wd_eilat_messages) };
    var isEilatMode_active = undefined
    const ROOT = typeof window !== 'undefined' && window.Shopify?.routes?.root ? window.Shopify.routes.root : '/';

    /** @returns {Promise<CartJson>} */
    async function getCart() {
        const res = await fetch(`${ROOT}cart.js`, { credentials: 'same-origin' });
        const cart = await res.json();
        // update eilat mode if it changed
        const old_eilat_mode = isEilatMode_active;
        isEilatMode_active = cart?.attributes?.[CART_ATTRIBUTE_EILAT_KEY] === EILAT_MODE_VALUE;
        try {
            sessionStorage.setItem('eilatMode', isEilatMode_active ? 'true' : 'false');
        } catch (_) {}
        if (old_eilat_mode != isEilatMode_active)
            setEilatMode(isEilatMode_active, cart);
        return cart;
    }

    /**
     * Fetch which variant IDs have eilat_price metafield (price > 0) via Storefront API.
     * @param {number[]} variantIds - cart line item variant IDs
     * @returns {Promise<Set<number>|null>} Set of variant ids with eilat price, or null to use line-item property fallback
     */
    async function fetchVariantIdsWithEilatPrice(variantIds) {
        const token = window.wd_sf_access_token;
        const shop = window.Shopify?.shop;
        if (!token || !shop || !variantIds || variantIds.length === 0)
            return null;

        const ids = [...new Set(variantIds)].map((id) => `gid://shopify/ProductVariant/${id}`);
        const query = `query CartVariantsEilat($ids: [ID!]!) {
            nodes(ids: $ids){
                ... on ProductVariant {
                    id metafield(namespace: "${EILAT_METAFIELD_NAMESPACE}", key: "${EILAT_METAFIELD_KEY}") { value }
                }
            } 
         }`;
        try {
            const res = await fetch(`https://${shop}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token, },
                body: JSON.stringify({ query, variables: { ids } }),
            });
            const json = await res.json();
            if (json.errors || !json.data?.nodes)
                return null;
            const set = new Set();
            for (const node of json.data.nodes) {
                if (!node?.id || !node.metafield?.value)
                    continue;
                let price = 0;
                try {
                    const parsed = JSON.parse(node.metafield.value);
                    price = typeof parsed?.price === 'number' ? parsed.price : Number(parsed?.price) || 0;
                } catch (_) {
                    const n = Number(node.metafield.value);
                    if (!Number.isNaN(n))
                        price = n;
                }
                if (price > 0) {
                    const numId = parseInt(node.id.replace(/.*\/(\d+)$/, '$1'), 10);
                    if (!Number.isNaN(numId)) set.add(numId);
                }
            }
            return set;
        } catch (_) {
            return null;
        }
    }

    /**
     * Set or clear Eilat mode on the cart.
     * used internally only in:
     * - canAddProduct_checks: auto change mode if empty
     * - notice: change mode after user confirmation
     * @param {boolean} enabled - true = set eilat, false = clear
     * @param {CartJson} cart - cart to update (optional)
     * @param {{ hasEilatPrice: boolean, asMode: "eilat" | "regular", variantId?: string, productType?: string} | undefined} opts - options for the mode
     * @returns {Promise<void>}
     */
    async function setEilatMode(enabled, cart, atc_opts) {
        let skipLocationCartUpdate = false;

        if (enabled != isEilatMode_active) {
            const updatedAttributes = { [CART_ATTRIBUTE_EILAT_KEY]: enabled ? EILAT_MODE_VALUE : '' };

            // Same occasion as mode flip: fold preferred-store location into this cart/update.js
            const locator = document.querySelector('my-store-locator');
            const replacement = locator?.wdPeekPreferredStoreForEilatMode?.(enabled);
            if (replacement?.name) {
                updatedAttributes._selected_location = replacement.name;
                skipLocationCartUpdate = true;
            }

            const payload = { attributes: updatedAttributes };

            /**
             * ================================================
             * Legacy Eilat filtering on mode change;
             * 
             * intensionally false - we dont use this BL no more - kept for legacy - in case we need to revert to it;
             * The legacy code is not fully working; it cannot handle removing bundle linked in;
             * it doesnt handle eilat/regulat bundle state on cart change (it doesnt add / remove per mode bundles on change)
             * we technicly CAN do it - but it invloves other logic and case multiple part of our code to implment real similar logic which is not really maintainable
             * ================================================
             */
            // when moving INTO eilat mode - remove all non-eilat products (by metafield or fallback to line-item property)
            if (false && enabled) {
            //#region Legacy Eilat filtering on mode change
                if (!cart)
                    cart = await getCart();

                // remove non eilat products
                const variantIdsWithEilat = (await fetchVariantIdsWithEilatPrice(cart.items.map((i) => i.variant_id))) || new Set();
                const nonEilatProducts = cart.items.filter((i) => !variantIdsWithEilat.has(i.variant_id))
                /** @type {Record<string, number>} */
                payload.updates = nonEilatProducts.reduce((acc, product) => ({ ...acc, [product.key]: 0 }), {});

                // remove apple care without parent
                const appleCareProducts = cart.items.filter((i) => i.properties["_sw_main_product_handle"] || i.properties["_aplcr_main_product_handle"])
                const appleCare_toRemove = appleCareProducts.filter((i) => {
                    const linkedProduct = cart.items.find(_i => _i.properties._set_number == i.properties._set_number);
                    return nonEilatProducts.find(_i => _i.key == linkedProduct.key);
                })
                payload.updates = {
                    ...payload.updates,
                    ...appleCare_toRemove.reduce((acc, product) => ({ ...acc, [product.key]: 0 }), {})
                }

                // remove trade-in products
                const tradeInProducts = cart.items.filter((i) => i.properties["_isTradeInProduct"]);
                payload.updates = {
                    ...payload.updates,
                    ...tradeInProducts.reduce((acc, product) => ({ ...acc, [product.key]: 0 }), {})
                }

                // allow one per product type (except accessories & apple care): keep highest-price item at qty 1, set rest to 0
                const limitedItems = cart.items.filter(
                    (i) => payload.updates[i.key] !== 0 && utils_isProductLimited(i.product_type)
                );
                // group by type
                const byType = /** @type {Record<string, typeof cart.items>} */ ({});
                for (const i of limitedItems) {
                    const t = i.product_type;
                    if (!byType[t])
                        byType[t] = [];
                    byType[t].push(i);
                }
                // for each type, keep highest-price item at qty 1, set rest to 0
                for (const items of Object.values(byType)) {
                    // if there is only one item, keep it at qty 1 if its not already 1
                    if (items.length == 1) {
                        if (items[0].quantity > 1)
                            payload.updates[items[0].key] = 1;
                        continue;
                    }
                    // sort by price
                    items.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
                    // keep highest-price item at qty 1, set rest to 0
                    payload.updates[items[0].key] = 1;
                    for (let j = 1; j < items.length; j++)
                        payload.updates[items[j].key] = 0;
                }

                // if adding limited product to eilat mode - remove all other products from same type
                if (atc_opts) {
                    const { hasEilatPrice, asMode, variantId, productType } = atc_opts || {};
                    if (utils_isProductLimited(productType)) {
                        cart.items.filter((i) => i.product_type == productType)
                            .forEach((i) => {
                                payload.updates[i.key] = 0;
                            });
                    }
                }
                //#endregion
            }

            // replaces legacy code: wipe clean the cart
            if (!cart)
                cart = await getCart();
            payload.updates = cart.items.reduce((updates, item) => {
                updates[item.key] = 0;
                return updates;
            }, {});

            await fetch(`${ROOT}cart/update.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify(payload)
            });
        }

        // update state
        isEilatMode_active = enabled;

        if (enabled) document.body.classList.add('eilatMode');
        else document.body.classList.remove('eilatMode');
        try {
            sessionStorage.setItem('eilatMode', enabled ? 'true' : 'false');
        } catch (_) {}

        // Wait for listeners (UI preferred-store sync). Location cart attrs may already be in the update above.
        const syncPromises = [];
        window.dispatchEvent(new CustomEvent('wd:eilat-mode-changed', {
            detail: {
                enabled,
                skipLocationCartUpdate,
                waitFor(p) {
                    if (p != null) syncPromises.push(Promise.resolve(p));
                },
            },
        }));
        if (syncPromises.length) await Promise.all(syncPromises);
    }

    /** main handler for preps before adding product to cart (both eilat mode and regular mode)
     * @param {CartJson} cart
     * @param {{ hasEilatPrice: boolean, asMode: "eilat" | "regular", variantId?: string, productType?: string }} opts
    */
    async function canAddProduct(cart, opts = { asMode: 'regular' }) {
        const { allowed, reason } = await canAddProduct_checks(cart, opts);
        if (allowed)
            return allowed;

        additiveHTML = '';
        if (reason == "one_per_type") {
            const sameTypeProduct = cart.items.find((i) => i.product_type == opts.productType);
            if (sameTypeProduct.variant_id != opts.variantId)
                additiveHTML = `<br> כרגע בעגלה:
            <a href="${sameTypeProduct.url}" target="_blank" style="color: var(--color-secondary-bt-label); cursor: pointer; text-decoration: underline;">מוצר מסוג ${opts.productType}</a>`;
        }

        return await notice(reason, cart, additiveHTML, opts);
    }

    /**
     * check if adding product is allowed in the current mode,
     * updates cart mode if needed (only for empty cart)
     * @param {CartJson} cart
     * @param {{ hasEilatPrice: boolean, asMode: "eilat" | "regular", variantId?: string, productType?: string}} opts
     * @returns {Promise<{ allowed: boolean, reason?: string }>}
     */
    async function canAddProduct_checks(cart, opts) {
        const { hasEilatPrice, asMode, productType } = opts || {};
        const isLimited = utils_isProductLimited(productType);
        /* block adding non-eilat product in eilat mode */
        if (asMode == 'eilat' && !hasEilatPrice)
            return { allowed: false, reason: 'not_eilat_product' };

        const cartHasItems = cart.items.length > 0;

        /* on adding product to {regular} mode & the cart is in {eilat} mode */
        if (asMode == "regular" && isEilatMode_active) {
            if (cartHasItems)
                return { allowed: false, reason: 'leave_eilat' };
            else
                // if cart has no items, set eilat mode to false
                await setEilatMode(false, cart)
        }

        /* on adding product to {eilat} mode & the cart is in {regular} mode */
        if (asMode == "eilat" && !isEilatMode_active) {
            // if cart has items, block adding product to eilat mode
            if (cartHasItems)
                return { allowed: false, reason: 'join_eilat' };
            else
                // if cart has no items, set eilat mode to true
                await setEilatMode(true, cart)
        }

        /* block adding multiple products of the same type in eilat mode if isLimited is true */
        if (asMode == "eilat" && isLimited) {
            const sameTypeProducts = cart.items.find((i) => i.product_type == productType);
            if (sameTypeProducts)
                return { allowed: false, reason: 'one_per_type' };
        }

        return { allowed: true };
    }

    /**
 * Show prompt: for cart mode related dialogs
 * @param {"not_eilat_product" | "leave_eilat" | "join_eilat" | "one_per_type"} reason - reason for the dialog
 * @param {CartJson} cart - cart to update (optional)
 * @returns {Promise<boolean>}
 */
    async function notice(reason, cart, additiveHTML, opts) {
        const modal = document.getElementById(CART_MODE_MODAL_ID);
        if (!modal) {
            setUpModal();
            return notice(reason, cart, additiveHTML);
        }
        const confirmButton = modal.querySelector('.wd-confirm-ok');
        const cancelButtons = modal.querySelectorAll('.wd-confirm-cancel');
        const exitButtons = document.querySelectorAll('.wd-eilat-exit-btn');

        // show / hide confirm button based on reason
        const showConfirm = ["leave_eilat", "join_eilat"].includes(reason);
        confirmButton.classList.toggle("hidden", !showConfirm);

        modal.querySelector('p').innerHTML = (MESSAGES[reason] ?? DEFAULT_MESSAGES[reason]) + (additiveHTML ?? "");

        // prompt user to confirm or cancel (opener for focus trap)
        modal.show(document.activeElement || undefined);

        return new Promise((resolve) => {
            function cleanup() {
                confirmButton.removeEventListener('click', confirm);
                cancelButtons.forEach(button => button.removeEventListener('click', close));
                exitButtons.forEach(button => button.disabled = false);
                document.body.removeEventListener('modalClosed', close);
                modal.hide();
            }

            async function confirm() {
                // update cart mode, move / join eilat mode
                confirmButton.innerHTML = `<div class="loading-overlay__spinner">
  <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
  </svg>
</div>`
                confirmButton.disabled = true;
                await setEilatMode(reason == "leave_eilat" ? false : true, cart, opts);
                confirmButton.innerHTML = "אישור";
                confirmButton.disabled = false;
                cleanup();
                resolve(true);
            }
            function close() {
                cleanup();
                resolve(false);
            }

            document.body.addEventListener('modalClosed', close, { once: true });

            if (showConfirm)
                confirmButton.addEventListener('click', confirm, { "once": true });
            cancelButtons.forEach(button => button.addEventListener('click', close, { "once": true }));
        });
    }

    /** patch fetch to check if the add to cart process is allowed in the current mode */
    function patchFetch() {
        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
            const [url, options] = args;
            const asMode = options?.buyMode || 'regular';
            if (url.includes('cart/add.js')) {
                /** @type {CartJson} */
                let payload = {};

                // try to parse the payload or fallback to regular fetch if invalid
                try {
                    payload = options?.body ? JSON.parse(options.body) : {};
                } catch (_) {
                    return originalFetch(url, options);
                }

                // block trade-in in eilat mode
                if (asMode == "eilat") {
                    const hasTradeIn = payload.items.some(item => item.properties && item.properties["_isTradeInProduct"]);
                    if (hasTradeIn) {
                        notice("not_eilat_product")
                        throw new Error('Cannot use trade-in in eilat mode.');
                    }
                }

                // ignore apple care items
                const isAppleCareItem = payload.items.every(item => item.properties["_sw_main_product_handle"] || item.properties["_aplcr_main_product_handle"]);
                if (!isAppleCareItem) {
                    const cart = await getCart();
                    for (const item of payload.items) {
                        opts = getPdpEilatOpts(asMode, item.id, item.product_type);
                        const isEilatValid = await canAddProduct(cart, opts);
                        if (!isEilatValid)
                            throw new Error('User choose not to continue the add to cart process.');
                    }
                }

                // add to cart transform on eilat mode - remove multiple items from the same type & set quantity to 1
                if (asMode == "eilat") {
                    const typeAlreadyAdded = new Set();
                    payload.items = payload.items.filter((item) => {
                        // ignore filtering unneeded types
                        if (!utils_isProductLimited(item.product_type))
                            return true;
                        // if this type is limited & already been added - ignore
                        if (typeAlreadyAdded.has(item.product_type))
                            return false;
                        // add type to set to check for future items
                        typeAlreadyAdded.add(item.product_type);
                        return true;
                    }).map((item) => {
                        // if item is ignored - add as is
                        if (!utils_isProductLimited(item.product_type))
                            return item;
                        // if item is not ignored - change quantity to 1
                        return { ...item, quantity: 1 };
                    });
                }

                // cleanup remove added product type (reduce request size)
                payload.items.forEach((item) => { delete item.product_type; })

                // re-write body with the transformed payload
                options.body = JSON.stringify(payload);

            }
            return originalFetch(url, options);
        }
    }

    /** creates modal for eilat mode dialogs */
    function setUpModal() {
        const modalHtml = `
            <modal-dialog id="${CART_MODE_MODAL_ID}" class="product-popup-modal">
            <div role="dialog" aria-modal="true" class="product-popup-modal__content" tabindex="-1">
                <button id="ModalClose-cart-mode-modal" type="button" class="product-popup-modal__toggle" aria-label="Close">x</button>
                <h2>שים לב</h2>
                <div class="product-popup-modal__content-info">
                <p>Your message here</p>
                <div class="wd-modal-buttons show_buy">
                    <button type="button" class="button button--secondary wd-confirm-cancel">ביטול</button>
                    <button type="button" class="button button--primary wd-confirm-ok">אישור</button>
                </div>
                </div>
            </div>
            </modal-dialog>`;
        const wrap = document.createElement('div');
        wrap.innerHTML = modalHtml.trim();
        const modal = wrap.firstElementChild;
        document.body.appendChild(modal);
    }

    /** Build eilat opts for PDP; form should have data-has-eilat-price */
    function getPdpEilatOpts(asMode, variantId, productType) {
        var $form = $('form[data-type="add-to-cart-form"]');
        const opt = {
            hasEilatPrice: $form.data('has-eilat-price') === true,
            asMode: asMode || 'regular',
            variantId,
            productType: productType,
        }
        return opt;
    }

    function utils_isProductLimited(productType) {
        const IGNORE_TYPE = ['accessories', 'applecare', ""];
        return !IGNORE_TYPE.includes(productType?.toLowerCase() || "");
    }

    // initialize eilat mode handler
    (async () => {
        patchFetch();
        setUpModal();
        await getCart();
    })();

    return {
        CART_ATTRIBUTE_EILAT_KEY,
        EILAT_MODE_VALUE,
        isEilatMode_active: () => isEilatMode_active,
        getCart,
        canAddProduct,
        setEilatMode,
        notice
    };
})();

if (typeof window !== 'undefined') {
    window.Eilat_handler = Eilat_handler;
}


/** @typedef {{
 *  attributes: Record<string, string>,
 *  items: {id: string,variant_id: string,key: string,properties: itemProperties, quantity: number, product_type: string}[],
 * }} CartJson */

/** @typedef {{
 * _set_number: string,
 * _uuid: string,
 * _isTradeInProduct: string,
 *  _sw_main_product_handle: string,
 *  _aplcr_main_product_handle: string,
 * }} itemProperties */

/** @typedef {{
 *  id: string,
 *  quantity: number,
 *  product_type: string,
 * }} CartItemJson */
